/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { TextField, Tooltip, InputAdornment, MenuItem } from "@material-ui/core";
import { SET_VALUE_CALLBACK_DEBOUNCE_RATE } from "../../utility/constants";

/**
 * This component is used to store the value of a field until the field losses focus. Then the value is returned to the parent component.
 * This cuts down dramatically on the amount of total form re-renders needed.
 *
 * @param {(String, Any) => void} setConfigValue Callback method to set the value entered in the text field to whatever given field in the chat config.
 * @param {String} startingValue The current value of the field in the chat config object.
 * @param {String} valueName The name of the field in the chat config object.
 * @param {String} label The label to be shown on the page.
 * @param {String} type The type of input to accept in the text field.
 * @param {(Boolean) => void} setEdited A callback method to set the form as edited. Called only the first change and if the form is not already marked as edited.
 * @param {Boolean} formEdited A boolean indicating whether or not the form has already been edited.
 * @param {Boolean} required A boolean indicating whether or not the field is required.
 * @param {String} errorMessage The error message to be shown when field is empty if it is required.
 * @param {String} toolTipMessage The message to be shown in the tooltip when the field is hovered over.
 * @param {Boolean} disabled A boolean indicating whether or not the field is disabled.
 * @param {Object} styleObject Style override
 * @param {Array} selectOptions Array of option objects in the format of  {id, label, disabled?},
 * @param {(any) => boolean} errorCheck Validation function,
 * @param {Boolean} enableDebounce A boolean indicating wether the data should be debounced back to the parent component when a user momentarily stops typing.
 */
const FormSelectField = ({
	setConfigValue,
	startingValue,
	valueName,
	label,
	type,
	setEdited,
	formEdited,
	required,
	errorMessage,
	toolTipMessage,
	disabled,
	styleObject,
	selectOptions,
	errorCheck,
	enableDebounce,
}) => {
	const [initialValue, setInitialValue] = React.useState(startingValue);
	const [value, setValue] = React.useState(startingValue);
	const [configValueCallBackDebounce, setConfigValueCallBackDebounce] = React.useState(undefined);
	const error = (errorCheck && errorCheck(value)) || (required && value.length === 0);

	//This check is to make sure we populate value with the newest value being
	// passed in if the config has changed.
	if (initialValue !== startingValue) {
		setValue(startingValue);
		setInitialValue(startingValue);
	}
	const onBlur = (event) => {
		const edited = startingValue !== event.target.value;
		if (edited) {
			setConfigValue(valueName, value);
		}
	};

	const onChange = (event) => {
		if (enableDebounce) {
			if (configValueCallBackDebounce) clearTimeout(configValueCallBackDebounce);
			const val = event.target.value;
			setConfigValueCallBackDebounce(
				setTimeout(() => {
					setConfigValueCallBackDebounce(undefined);
					setConfigValue(valueName, val);
				}, SET_VALUE_CALLBACK_DEBOUNCE_RATE),
			);
		}
		if (!formEdited) setEdited(true);
		setValue(event.target.value);
	};
	const style = styleObject || { margin: "25px", minWidth: "40%" };
	const helperText = error ? errorMessage : "";
	const reactInputProps = {};
	const materialUIInputProps = {};
	if (toolTipMessage) {
		materialUIInputProps["startAdornment"] = (
			<InputAdornment position="start">
				<Tooltip arrow interactive placement={"bottom-start"} title={toolTipMessage}>
					<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
				</Tooltip>
			</InputAdornment>
		);
	}
	if (type === "color") {
		materialUIInputProps["disableUnderline"] = true;
		reactInputProps["style"] = { height: "30px" };
	}
	return (
		<>
			<TextField
				label={label}
				style={style}
				type={type}
				error={error}
				disabled={disabled}
				helperText={helperText}
				value={value}
				inputProps={reactInputProps}
				InputProps={materialUIInputProps}
				onChange={onChange}
				onBlur={onBlur}
				select
			>
				{(selectOptions || []).map((option) => {
					return (
						<MenuItem key={option.id} value={option.id} disabled={option.disabled}>
							{option.label}
						</MenuItem>
					);
				})}
			</TextField>
		</>
	);
};

export default FormSelectField;
